import type { LucideIcon, LucideProps } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ## Icons that have to turn around in Arabic
 *
 * An icon that points along the reading direction means "back", "forward" or
 * "return" — and in an RTL layout it means the opposite of what it draws unless
 * it is mirrored. The dashboard already knew this and applied it by hand, which
 * is why it is applied three different ways and missed twice: eleven call sites
 * mirror (six with `rtl:-scale-x-100`, five with `rtl:rotate-180`, which are not
 * the same transform), and `Spaces/TableQrSheet` and `Spaces/FloorMapEditor`
 * mirror nothing at all while `Operations/Kitchen` mirrors the same glyph.
 *
 * `-scale-x-100` is the correct primitive and `rotate-180` is a coincidence: the
 * two agree only for a glyph that is also symmetric top-to-bottom. A chevron is,
 * so both look right; `Undo2` is not, and rotating it draws a redo arrow.
 *
 * The set is keyed on lucide's own `displayName`, so it costs no imports and
 * covers icons no screen has used yet.
 *
 * Deliberately NOT in the set, because the direction is not the meaning:
 *   - `TrendingUp` / `TrendingDown` and the chart glyphs — they read against a
 *     time axis, and mirroring one would invert the trend it reports.
 *   - `RotateCcw` / `RotateCw` — a reset, not a movement. Mirroring turns one
 *     into the other, which is how you get an undo button drawn as redo.
 *   - `Truck`, and the other category glyphs — a static noun in a nav rail is
 *     not pointing anywhere.
 *   - The media transport icons, which stay LTR in Arabic by convention.
 */
const DIRECTIONAL: ReadonlySet<string> = new Set([
  'ArrowLeft', 'ArrowRight', 'ArrowBigLeft', 'ArrowBigRight',
  'ArrowLeftToLine', 'ArrowRightToLine', 'ArrowLeftFromLine', 'ArrowRightFromLine',
  'MoveLeft', 'MoveRight',
  'ChevronLeft', 'ChevronRight', 'ChevronsLeft', 'ChevronsRight',
  'CircleChevronLeft', 'CircleChevronRight', 'SquareChevronLeft', 'SquareChevronRight',
  'CornerDownLeft', 'CornerDownRight', 'CornerUpLeft', 'CornerUpRight',
  'Undo2', 'Redo2', 'Reply', 'ReplyAll', 'Forward', 'Send',
  'LogIn', 'LogOut',
  'PanelLeft', 'PanelRight', 'PanelLeftOpen', 'PanelLeftClose',
  'PanelRightOpen', 'PanelRightClose',
]);

/** Whether this glyph reverses in RTL. Exported so the rule is testable. */
export function isDirectional(glyph: LucideIcon): boolean {
  return DIRECTIONAL.has(glyph.displayName ?? '');
}

/** `md` is 16px because 225 of the app's 250 icons are already that size. */
const SIZES = {
  xs: 'h-3 w-3',
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  xl: 'h-6 w-6',
} as const;

export interface IconProps extends Omit<LucideProps, 'ref' | 'size'> {
  /** The lucide glyph to draw. */
  as: LucideIcon;
  size?: keyof typeof SIZES;
  /**
   * `auto` mirrors the glyph in RTL when it is one of the directional ones.
   * `never` is for a glyph that happens to be in the set but is being used as a
   * fixed label — a literal "left" in a floor-map inspector, not a "back".
   */
  mirror?: 'auto' | 'never' | 'always';
}

/**
 * One place that decides how big an icon is and which way it faces.
 *
 * Left alone deliberately: lucide's default stroke weight. The Figma file draws
 * its icons at 1.5 in a 16 grid, which is heavier than the 1.33 the same glyph
 * renders at through lucide's 24 grid at `h-4`. Matching it is a visible change
 * to all 69 existing icon call sites at once, and a stroke weight is a design
 * decision rather than a defect — see the report.
 */
export function Icon({ as: Glyph, size = 'md', mirror = 'auto', className, ...props }: IconProps) {
  const mirrored = mirror === 'always' || (mirror === 'auto' && isDirectional(Glyph));
  return <Glyph className={cn(SIZES[size], mirrored && 'rtl:-scale-x-100', className)} {...props} />;
}
